// Folders (Workspace 2, screen 4): the engagement file as the engine publishes it (file.tree → file_nodes), every folder and
// subfolder — phases 00–09, their step folders (02.00 Preliminary Analysis included), audit programs and their Evidence and Tasks —
// with a status dot and final/total from the workpaper index, and the selected folder: its header (what it waits on, counts,
// owner) and sections: Workpapers (the index's, plus registered workpapers the engine lists on the folder such as PA-4 or AP-5.1),
// Subfolders, Files (what the engine expects there), the Evidence folder's documents, client Evidence and Tasks. The page follows
// the engine live (ADR-0032): it reloads when file_nodes, wp_index or pbc_index are published and says when that was.
import { Link, useNavigate, useParams } from "react-router-dom";
import { EmptyState, LoadingRows, StatusPill, Tree, type TreeNode } from "../../components";
import { useApp } from "../../app/context";
import { useLiveTables } from "../../data/live";
import type { FileNode, WpIndexRow } from "../../data/types";
import { PHASES, groupBy, phaseName, phaseSummary, wpTypeLabel } from "../../lib/workspace";
import { ItemAction, SignMarks, SubkindChip, WaitIcons, useWs, useWsAct } from "./common";
import { LiveStamp } from "./LiveStamp";

const segs = (path: string) => path.replace(/^File\//, "").split("/").filter(Boolean);
/** The folder under the phase ("05.01 Cash (AP-5)") from the engine's folder path. */
export const subfolderOf = (w: Pick<WpIndexRow, "folder" | "phase">) => {
  const parts = segs(w.folder);
  return parts.length > 1 ? parts[1] : "";
};
/** Phase code and step/program folder of an engine path ("File/02 Planning/02.00 Preliminary Analysis/…" → 02, "02.00 Preliminary Analysis"). */
export const placeOf = (path: string) => {
  const parts = segs(path);
  return { phase: (parts[0] ?? "").slice(0, 2), folder: parts[1] ?? "" };
};
/** Everything under the phase folder, as a path ("AP-5 Cash/Evidence"); "" for the phase itself. */
export const relOf = (path: string) => segs(path).slice(1).join("/");
const within_ = (rel: string, folder: string) => !folder || rel === folder || rel.startsWith(folder + "/");

/** What a folder holds besides workpapers (file.tree), at any depth under it: the files the engine expects there (generated
 * workbooks, the preliminary analysis, registered workpapers, filed documents) and the documents placed in its Evidence folders. */
export function filesOf(nodes: FileNode[], phase: string, folder: string) {
  const inFolder = (path: string) => placeOf(path).phase === phase && within_(relOf(path), folder);
  const files = nodes.filter((n) => n.kind === "file" && n.parent && inFolder(n.parent)).sort((a, b) => a.path.localeCompare(b.path, undefined, { numeric: true }))
    .map((n) => ({ ...n, where: relOf(n.parent!) === folder ? null : relOf(n.parent!).slice(folder ? folder.length + 1 : 0) || null }));
  const evidence = nodes.filter((n) => n.kind === "evidence" && (n.documents?.length ?? 0) > 0 && inFolder(n.path))
    .flatMap((n) => (n.documents ?? []).map((d) => ({ ...d, folder: n.path })));
  return { files, evidence };
}

export function FolderFiles({ files, evidence }: ReturnType<typeof filesOf>) {
  return (
    <>
      <section aria-labelledby="f-files" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h3 id="f-files" className="border-b border-line px-4 py-2 text-sm font-semibold">Files · {files.length}</h3>
        {files.length === 0 ? <p className="px-4 py-4 text-sm text-muted">No files filed in this folder yet.</p> : (
          <ul className="divide-y divide-line" aria-label="Files in this folder">
            {files.map((f) => (
              <li key={f.path} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1 break-words">{f.label}{f.where && <span className="text-xs text-muted"> · in {f.where}</span>}</span>
                <span className="text-xs text-muted">{f.file?.what ?? "file"}</span>
                {(!f.exists || f.file?.problem) && <StatusPill state="missing" label={f.file?.problem ? titleCaseWord(f.file.problem) : "Missing"} />}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="f-evdocs" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h3 id="f-evdocs" className="border-b border-line px-4 py-2 text-sm font-semibold">Evidence folder · {evidence.length} document{evidence.length === 1 ? "" : "s"}</h3>
        {evidence.length === 0 ? <p className="px-4 py-4 text-sm text-muted">No documents placed in this folder's Evidence yet.</p> : (
          <ul className="divide-y divide-line" aria-label="Documents in the Evidence folder">
            {evidence.map((d) => (
              <li key={`${d.folder}/${d.ref}/${d.name}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1 break-words"><span className="font-mono text-xs text-muted">{d.ref}</span> {d.name}</span>
                {(!d.exists || d.problem) && <StatusPill state="missing" label={d.problem ? titleCaseWord(d.problem) : "Missing"} />}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
const titleCaseWord = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** One folder of the tree: its id (`<phase>/<path under the phase>`), the engine's node when published, and the index rows under it. */
export interface FolderEntry { id: string; parent: string | null; phase: string; rel: string; name: string; node: FileNode | null }

/** Every folder to show: each phase; every published folder node under it (step folders, programs, Evidence, Tasks and deeper —
 * never files, which the pane lists); and any folder the workpaper index names that the tree has not published. Phase 08 is
 * shown by client document type instead (the client documents view). */
export function folderEntries(files: FileNode[], wp: WpIndexRow[]): FolderEntry[] {
  const out = new Map<string, FolderEntry>();
  for (const p of PHASES) out.set(p.code, { id: p.code, parent: null, phase: p.code, rel: "", name: p.name, node: files.find((n) => n.kind === "phase" && placeOf(n.path).phase === p.code) ?? null });
  const add = (phase: string, rel: string, node: FileNode | null) => {
    if (!rel || phase === "08" || !out.has(phase)) return;
    const parts = rel.split("/");
    for (let i = 1; i <= parts.length; i++) {
      const r = parts.slice(0, i).join("/");
      const id = `${phase}/${r}`;
      const own = i === parts.length ? node : null;
      const had = out.get(id);
      if (had) { if (own && !had.node) had.node = own; continue; }
      out.set(id, { id, parent: i === 1 ? phase : `${phase}/${parts.slice(0, i - 1).join("/")}`, phase, rel: r, name: parts[i - 1], node: own });
    }
  };
  for (const n of files) if (n.kind !== "file" && n.kind !== "phase") add(placeOf(n.path).phase, relOf(n.path), n);
  for (const w of wp) add(w.phase, relOf(w.folder), null);
  return [...out.values()];
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
/** What a folder without workpapers holds, in words: its files, the documents in its Evidence, or the engine's item count. */
export const countLabel = (files: number, docs: number, items: number) =>
  files && docs ? plural(files + docs, "item") : files ? plural(files, "file") : docs ? plural(docs, "document") : items ? plural(items, "item") : "empty";

const DOT: Record<string, string> ={ done: "bg-done", blocked: "bg-blocked", in_progress: "bg-progress", not_started: "bg-neutral-line", not_applicable: "bg-neutral-line" };
const wpUnder = (wp: WpIndexRow[], phase: string, folder: string) => wp.filter((w) => w.phase === phase && within_(relOf(w.folder), folder));

export function WsFolders() {
  const ws = useWs();
  const { ds } = useApp();
  const live = useLiveTables(ds, ws.id, ["file_nodes", "wp_index", "pbc_index"], ws.reload);
  const nav = useNavigate();
  const { "*": splat = "" } = useParams();
  const { open, drawer } = useWsAct(ws.reload);
  const [phase = "", ...sub] = splat.split("/").filter(Boolean);
  const folder = sub.length ? decodeURIComponent(sub.join("/")) : "";
  const selected = phase ? (folder ? `${phase}/${folder}` : phase) : "";

  const entries = folderEntries(ws.files, ws.wp);
  const nodes: TreeNode[] = [];
  for (const e of entries) {
    if (!e.parent) {
      const s = phaseSummary(ws.wp.filter((w) => w.phase === e.phase));
      const count = e.phase === "08" ? `${ws.pbc.filter((d) => d.status === "processed").length}/${ws.pbc.length}` : `${s.final}/${s.total}`;
      nodes.push({ id: e.id, parent: null, textLabel: `${e.phase} ${e.name}, ${count} done`, label: <FolderLabel dot={DOT[s.state]} code={e.phase} name={e.name} count={count} /> });
      if (e.phase === "08") {
        for (const [t, docs] of groupBy(ws.pbc, (d) => d.docTypeLabel)) nodes.push({ id: `08/${t}`, parent: "08", textLabel: `${t}, ${docs.length} documents`, label: <FolderLabel name={t} count={`${docs.filter((d) => d.status === "processed").length}/${docs.length}`} /> });
      }
      continue;
    }
    const rows = wpUnder(ws.wp, e.phase, e.rel);
    if (rows.length) {
      const fs = phaseSummary(rows);
      nodes.push({ id: e.id, parent: e.parent, textLabel: `${e.name}, ${fs.final} of ${fs.total} final`, label: <FolderLabel dot={DOT[fs.state]} name={e.name} count={`${fs.final}/${fs.total}`} /> });
    } else {
      const k = filesOf(ws.files, e.phase, e.rel);
      const count = countLabel(k.files.length, k.evidence.length, e.node?.items ?? 0);
      nodes.push({ id: e.id, parent: e.parent, textLabel: `${e.name}, ${count}`, label: <FolderLabel name={e.name} count={count} /> });
    }
  }
  const go = (id: string) => { const [ph, ...rest] = id.split("/"); nav(`/e/${ws.id}/folders/${ph}${rest.length ? "/" + encodeURIComponent(rest.join("/")) : ""}`); };
  const openIds = phase ? [phase, ...folder.split("/").filter(Boolean).map((_, i, a) => `${phase}/${a.slice(0, i + 1).join("/")}`)] : ["05"];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-2">
        <div><h1 className="text-xl font-semibold">Folders</h1><p className="text-sm text-muted">The engagement file, folder by folder.</p></div>
        <LiveStamp live={live} />
      </header>
      <div className="flex flex-col gap-4 md:flex-row">
        <nav aria-label="File folders" className="rounded-lg border border-line bg-raised p-2 md:w-72 md:shrink-0">
          {ws.loading ? <LoadingRows rows={8} label="Loading folders" /> : <Tree nodes={nodes} selected={selected || undefined} onSelect={go} label="Engagement file" initiallyOpen={openIds} />}
        </nav>
        <section aria-label="Folder contents" className="min-w-0 flex-1">
          {ws.loading ? null : !phase ? <EmptyState title="Choose a folder">Pick a phase or folder on the left to see its workpapers, evidence and tasks.</EmptyState>
            : phase === "08" ? <ClientDocsFolder type={folder} />
            : <FolderPane phase={phase} folder={folder} entries={entries} open={open} />}
        </section>
      </div>
      {drawer}
    </div>
  );
}

function FolderLabel({ dot, code, name, count }: { dot?: string; code?: string; name: string; count: string }) {
  return (
    <span className="flex min-w-0 items-center gap-2 text-sm">
      {dot && <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />}
      <span className="min-w-0 flex-1 truncate">{code && <span className="font-mono text-muted">{code} </span>}{name}</span>
      <span className="shrink-0 text-xs tabular-nums text-muted">{count}</span>
    </span>
  );
}

/** Workpapers the engine lists on a folder (file.tree) that the workpaper index does not carry — a registered workpaper (PA-4,
 * AP-5.1, AP-5.1-IND) filed with workpaper.register-file. */
export function registeredOf(entries: FolderEntry[], wp: WpIndexRow[], phase: string, folder: string) {
  const indexed = new Set(wp.map((w) => w.workpaperId).filter(Boolean));
  const seen = new Set<string>();
  return entries.filter((e) => e.phase === phase && e.parent && within_(e.rel, folder)).flatMap((e) => (e.node?.workpapers ?? []).map((w) => ({ ...w, where: e.rel })))
    .filter((w) => !indexed.has(w.id) && !seen.has(w.id) && (seen.add(w.id), true));
}

function FolderPane({ phase, folder, entries, open }: { phase: string; folder: string; entries: FolderEntry[]; open: ReturnType<typeof useWsAct>["open"] }) {
  const ws = useWs();
  const entry = entries.find((e) => e.id === (folder ? `${phase}/${folder}` : phase));
  const name = entry?.name ?? (folder.split("/").pop() || phaseName(phase));
  const rows = wpUnder(ws.wp, phase, folder).sort((a, b) => a.ref.localeCompare(b.ref, undefined, { numeric: true }));
  const registered = registeredOf(entries, ws.wp, phase, folder);
  const children = entries.filter((e) => e.parent === (folder ? `${phase}/${folder}` : phase));
  const refs = new Set(rows.map((w) => w.ref));
  const s = phaseSummary(rows);
  const waiting = rows.find((w) => w.waitingOn)?.waitingOn;
  const owners = [...new Set(rows.map((w) => w.owner).filter(Boolean))] as string[];
  const docs = ws.pbc.filter((d) => d.neededBy.some((r) => refs.has(r)));
  const processed = docs.filter((d) => d.status === "processed"), issues = docs.filter((d) => d.status === "issue"),
    missing = docs.filter((d) => ["outstanding", "late", "draft"].includes(d.status));
  const tasks = ws.attention.filter((a) => a.ref && refs.has(a.ref));
  const kept = filesOf(ws.files, phase, folder);
  const total = rows.length + registered.length;
  return (
    <div className="space-y-4">
      <header className="space-y-1.5 rounded-lg border border-line bg-raised px-4 py-3">
        <p className="text-xs text-muted"><span className="font-mono">{phase}</span> {phaseName(phase)}{folder.includes("/") ? ` · ${folder.split("/").slice(0, -1).join(" / ")}` : ""}</p>
        <h2 className="text-lg font-semibold">{folder ? name : phaseName(phase)}</h2>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {waiting ? <StatusPill kind={waiting.kind === "client" ? "client" : "waiting"} label={`Waiting on ${waiting.ref}`} title={waiting.label} />
            : <StatusPill state={s.state} label={{ done: "All final", blocked: "Needs input", in_progress: "In progress", not_started: "Not started", not_applicable: rows.length || kept.files.length || registered.length ? "Filed" : "Empty" }[s.state]} />}
          <span className="text-muted">{s.final} of {s.total} final · {docs.length} document{docs.length === 1 ? "" : "s"} · {tasks.length} task{tasks.length === 1 ? "" : "s"}</span>
          {owners.length > 0 && <span className="text-muted">· Owner {owners.join(", ")}</span>}
        </div>
        {phase === "02" && folder.startsWith("02.00") && (
          <p className="text-sm"><Link className="text-brand hover:underline" to={`/e/${ws.id}/statements`}>Open the statements rebuilt from the books</Link>
            <span className="text-muted"> — the FS and FX workbooks of each run, on screen with the drill-down from a line to its accounts and ledger rows.</span></p>
        )}
      </header>
      <section aria-labelledby="f-wp" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h3 id="f-wp" className="border-b border-line px-4 py-2 text-sm font-semibold">Workpapers · {total}</h3>
        {total === 0 ? <p className="px-4 py-4 text-sm text-muted">No workpapers expected in this folder.</p> : (
          <ul className="divide-y divide-line">
            {rows.map((w) => (
              <li key={w.ref} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                <Link to={`/e/${ws.id}/wp/${encodeURIComponent(w.ref)}`} className="min-w-0 flex-1 hover:underline"><span className="font-mono text-xs text-muted">{w.ref}</span> {w.name}</Link>
                <span className="text-xs text-muted">{wpTypeLabel(w.type)}</span>
                <StatusPill state={w.status} label={w.statusLabel} />
                <SignMarks s={w.signoffs} />
                <WaitIcons w={w} />
              </li>
            ))}
            {registered.map((w) => (
              <li key={`reg-${w.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm" aria-label={`${w.id} ${w.title}, registered workpaper`}>
                <span className="min-w-0 flex-1"><span className="font-mono text-xs text-muted">{w.id}</span> {w.title.startsWith(w.id) ? w.title.slice(w.id.length).trim() : w.title}
                  {w.where !== folder && <span className="text-xs text-muted"> · in {w.where}</span>}</span>
                <span className="text-xs text-muted">Registered workpaper</span>
                <StatusPill state={w.stale ? "stale" : w.reviewed ? "reviewed" : w.prepared ? "ready_for_review" : w.rendered ? "in_progress" : "not_started"}
                  label={w.stale ? "Stale" : w.partner ? "Signed" : w.reviewed ? "Reviewed" : w.prepared ? "Prepared" : w.rendered ? "Filed" : "Not filed"} />
              </li>
            ))}
          </ul>
        )}
      </section>
      {children.length > 0 && (
        <section aria-labelledby="f-sub" className="overflow-hidden rounded-lg border border-line bg-raised">
          <h3 id="f-sub" className="border-b border-line px-4 py-2 text-sm font-semibold">Subfolders · {children.length}</h3>
          <ul className="divide-y divide-line" aria-label="Subfolders">
            {children.map((c) => {
              const k = filesOf(ws.files, c.phase, c.rel);
              const n = wpUnder(ws.wp, c.phase, c.rel).length;
              return (
                <li key={c.id} className="flex flex-wrap items-center gap-x-3 px-4 py-2 text-sm">
                  <Link className="min-w-0 flex-1 hover:underline" to={`/e/${ws.id}/folders/${c.phase}/${encodeURIComponent(c.rel)}`}>{c.name}</Link>
                  <span className="text-xs text-muted">{[n && `${n} workpaper${n === 1 ? "" : "s"}`, k.files.length && `${k.files.length} file${k.files.length === 1 ? "" : "s"}`,
                    k.evidence.length && `${k.evidence.length} document${k.evidence.length === 1 ? "" : "s"}`].filter(Boolean).join(" · ") || "empty"}</span>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      <FolderFiles {...kept} />
      {entry?.node && entry.node.items > kept.files.length + kept.evidence.length && (
        <p className="text-xs text-muted">The engine counts {entry.node.items} item{entry.node.items === 1 ? "" : "s"} expected in this folder; {kept.files.length + kept.evidence.length} {kept.files.length + kept.evidence.length === 1 ? "is" : "are"} listed here.</p>
      )}
      <section aria-labelledby="f-ev" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h3 id="f-ev" className="border-b border-line px-4 py-2 text-sm font-semibold">Evidence · {processed.length} processed · {issues.length} with an issue · {missing.length} missing</h3>
        {docs.length === 0 ? <p className="px-4 py-4 text-sm text-muted">No client documents are tied to these workpapers.</p> : (
          <ul className="divide-y divide-line">
            {docs.map((d) => (
              <li key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                <Link to={`/e/${ws.id}/documents#pbc-${d.id}`} className="min-w-0 flex-1 hover:underline"><span className="font-mono text-xs text-muted">{d.ref}</span> {d.title}</Link>
                {d.issue && <span className="text-xs text-blocked">{d.issue}</span>}
                <StatusPill state={d.status} />
              </li>
            ))}
          </ul>
        )}
      </section>
      <section aria-labelledby="f-tasks" className="overflow-hidden rounded-lg border border-line bg-raised">
        <h3 id="f-tasks" className="border-b border-line px-4 py-2 text-sm font-semibold">Tasks · {tasks.length}</h3>
        {tasks.length === 0 ? <p className="px-4 py-4 text-sm text-muted">Nothing waits on anyone in this folder.</p> : (
          <ul className="divide-y divide-line">
            {tasks.map((t) => (
              <li key={t.key} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm">
                <span className="min-w-0 flex-1">{t.title} <SubkindChip subkind={t.subkind} /></span>
                <ItemAction item={t} open={open} />
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ClientDocsFolder({ type }: { type: string }) {
  const ws = useWs();
  const docs = ws.pbc.filter((d) => !type || d.docTypeLabel === type);
  return (
    <div className="space-y-3">
      <header className="rounded-lg border border-line bg-raised px-4 py-3">
        <p className="text-xs text-muted"><span className="font-mono">08</span> Client documents</p>
        <h2 className="text-lg font-semibold">{type || "Client documents"}</h2>
        <p className="text-sm text-muted">{docs.filter((d) => d.status === "processed").length} of {docs.length} processed · <Link className="text-brand hover:underline" to={`/e/${ws.id}/documents`}>Open Client docs</Link></p>
      </header>
      <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised">
        {docs.map((d) => (
          <li key={d.id} className="flex flex-wrap items-center gap-x-3 px-4 py-2 text-sm">
            <span className="min-w-0 flex-1"><span className="font-mono text-xs text-muted">{d.ref}</span> {d.title}</span><StatusPill state={d.status} />
          </li>
        ))}
      </ul>
    </div>
  );
}
